import { useTranslation } from "@sagot/i18n";
import { Badge, Icon, StatusBadge } from "@sagot/ui";
import type { DefaultStatus } from "./defaults-model";

const D = "engage.workflows.defaults";

/** Neutral badge that marks a workflow as one of the defaults. Status is icon + words (rule 10). */
export function DefaultBadge() {
  const { t } = useTranslation();
  return (
    <Badge data-testid="default-badge">
      <Icon name="shield-check" className="size-3" />
      {t(`${D}.badge`)}
    </Badge>
  );
}

/** A signal (the amber accent, never an action): somebody changed the default, or the catalogue has a newer one. */
function SignalBadge({ icon, label, testId }: { icon: "pencil" | "arrow-up"; label: string; testId: string }) {
  return (
    <span className="badge bg-accent-soft text-accent-ink" data-testid={testId}>
      <Icon name={icon} className="size-3" />
      {label}
    </span>
  );
}

export function ModifiedBadge() {
  const { t } = useTranslation();
  return <SignalBadge icon="pencil" label={t(`${D}.modified`)} testId="modified-badge" />;
}

export function UpdateAvailableBadge() {
  const { t } = useTranslation();
  return <SignalBadge icon="arrow-up" label={t(`${D}.updateAvailable`)} testId="update-badge" />;
}

/** Live, paused, or an optional alert that nobody switched on yet. */
export function DefaultStateBadge({ status }: { status: DefaultStatus }) {
  const { t } = useTranslation();
  if (status === "live") return <StatusBadge status="success" label={t("engage.workflows.states.live")} />;
  if (status === "off") return <StatusBadge status="neutral" icon="circle" label={t(`${D}.offByDefault`)} />;
  return <StatusBadge status="neutral" icon="pause" label={t("engage.workflows.states.paused")} />;
}
